/**
 * UTF-8 byte length.
 *
 * Measuring with `.length` counts UTF-16 code units, which would let a
 * non-ASCII payload pass a size check and still exceed the cap on the wire.
 */
export function byteLength(value: string): number {
  return new TextEncoder().encode(value).length
}

export interface CapResult<T> {
  kept: T[]
  dropped: number
}

/**
 * Trims a list of captured entries to fit a byte budget, keeping the newest.
 *
 * Dropping from the front matters: the interesting part of a log is the last
 * thing that happened, so a head-preserving trim would discard the failure the
 * bundle was collected for. The count caps on each buffer are not enough on
 * their own — a single pathological entry (500 frames of minified stack) can
 * blow the budget by itself, and it is exactly the noisy session most worth
 * reading.
 */
export function capEntriesByBytes<T>(entries: T[], maxBytes: number): CapResult<T> {
  if (entries.length === 0) return { kept: [], dropped: 0 }
  if (byteLength(JSON.stringify(entries)) <= maxBytes) {
    return { kept: entries, dropped: 0 }
  }

  const kept: T[] = []
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const candidate = [entries[i] as T, ...kept]
    if (byteLength(JSON.stringify(candidate)) > maxBytes) break
    kept.unshift(entries[i] as T)
  }

  return { kept, dropped: entries.length - kept.length }
}
